import { Text } from '@thanx/uikit/text'
import { getValidation } from 'actions/validations'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { ValidationTypes } from 'models/Validation'
import React, { useCallback, useEffect, useMemo } from 'react'
import Blocks from 'scenes/Cms/components/Blocks'
import { useFeaturedContentCarousel } from 'scenes/Cms/Content/AppHomePage/hooks'
import {
  ContentBlockData,
  FeaturedContentCarouselBlockData,
  FeaturedContentSlideData,
} from 'slices/appLandingPageTheme/types'
import CarouselSlideContent from './CarouselSlideContent'

const t = buildTranslate(
  'cms.content.app_home_page.content.edit_block.content.featured_content_carousel'
)

type Props = {
  blockId: string
  contentBlock: ContentBlockData
}

const FeaturedContentCarouselContent = ({ blockId, contentBlock }: Props) => {
  const { addSlideToCarousel, removeSlideFromCarousel, reorderSlides } =
    useFeaturedContentCarousel()

  const dispatch = useDispatch()

  useEffect(() => {
    async function fetchData() {
      dispatch(getValidation({ type: ValidationTypes.AppHomePage }))
    }

    fetchData()
  }, [dispatch])

  const { featured_contents } = contentBlock as Pick<
    FeaturedContentCarouselBlockData,
    'featured_contents'
  >

  // Create slide order array for Blocks component, sorted by position
  const slideOrder = useMemo(() => {
    if (!featured_contents) return []

    return Object.entries(featured_contents)
      .map(([slideId, slideData]: [string, any]) => ({
        slideId,
        position: slideData?.position || 0,
      }))
      .sort((a, b) => a.position - b.position)
      .map(item => item.slideId)
  }, [featured_contents])

  const handleSetOrder = (newOrder: string[]) => {
    // Handle deletions
    const removedSlideId = slideOrder.find(id => !newOrder.includes(id))
    if (removedSlideId) {
      removeSlideFromCarousel(blockId, removedSlideId)
      return
    }

    // Handle reordering - only if the order actually changed
    if (JSON.stringify(newOrder) !== JSON.stringify(slideOrder)) {
      reorderSlides(blockId, newOrder)
    }
  }

  const renderSlide = (slideId: string, index: number) => {
    const slide = featured_contents?.[slideId]
    if (!slide) return null

    // Cast the slide to the expected type with default values
    const slideData: FeaturedContentSlideData = {
      type: slide.type,
      settings: slide.settings,
      image: slide.image || { id: null, url: '', uploading: false },
      url: slide.url || {
        type: 'external',
        external: '',
        inapp: '',
        authenticable: false,
      },
      position: slide.position || index,
    }

    return (
      <>
        <Text.Header4>Slide {index + 1}</Text.Header4>
        <CarouselSlideContent
          carouselId={blockId}
          slideId={slideId}
          slide={slideData}
        />
      </>
    )
  }

  const handleOnNewBlock = useCallback(() => {
    addSlideToCarousel(blockId)
  }, [addSlideToCarousel, blockId])

  if (contentBlock.type !== 'featured_content_carousel') return <></>

  return (
    <>
      <Text.Header3>{t('title')}</Text.Header3>
      <Text.BodyText1>{t('description')}</Text.BodyText1>

      <Blocks
        type="slide"
        order={slideOrder}
        setOrder={handleSetOrder}
        renderItem={renderSlide}
        minBlockCount={0}
        maxBlockCount={10}
        onNewBlock={handleOnNewBlock}
      />
    </>
  )
}

export default FeaturedContentCarouselContent
